/*
  Quote form enhancement for sections/kavrox-quote-form.liquid.
  - Validates on submit, shows inline errors and an error summary, focuses the first invalid field.
  - Keeps entered values in sessionStorage (this tab only, 30 minutes) so they come back after a
    server-side error. The privacy consent is never stored or restored.
  - Prevents duplicate submits and shows a sending state.
  Without JavaScript the form uses native browser validation and submits as normal.
*/
(function () {
  const STORAGE_PREFIX = 'kx-qform:';
  const MAX_AGE = 30 * 60 * 1000;
  const SAFETY_RESET = 15000;
  const PHONE_METHODS = ['phone', 'whatsapp'];

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function readStore(key) {
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* Storage unavailable (private mode or quota): values are simply not kept. */
    }
  }

  function clearStore(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      /* Nothing to clear. */
    }
  }

  function isStorable(element) {
    if (!element.name || element.disabled) return false;
    if (element.hasAttribute('data-kx-no-restore')) return false;
    if (['hidden', 'submit', 'button', 'file', 'password'].includes(element.type)) return false;
    return element.name.indexOf('contact[') === 0;
  }

  function addToken(element, attribute, token) {
    const tokens = (element.getAttribute(attribute) || '').split(' ').filter(Boolean);
    if (!tokens.includes(token)) tokens.push(token);
    element.setAttribute(attribute, tokens.join(' '));
  }

  function removeToken(element, attribute, token) {
    const tokens = (element.getAttribute(attribute) || '').split(' ').filter((t) => t && t !== token);
    if (tokens.length) {
      element.setAttribute(attribute, tokens.join(' '));
    } else {
      element.removeAttribute(attribute);
    }
  }

  function isHttpsUrl(value) {
    if (!/^https:\/\//i.test(value)) return false;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && Boolean(url.hostname);
    } catch (error) {
      return false;
    }
  }

  class QuoteForm {
    constructor(form) {
      this.form = form;
      this.storageKey = STORAGE_PREFIX + form.id;
      this.summary = form.querySelector('[data-kx-qform-summary]');
      this.submitButton = form.querySelector('[data-kx-qform-submit]');
      this.liveRegion = form.querySelector('[data-kx-qform-live]');
      this.flagged = new Set();
      this.resetTimer = null;

      const success = form.querySelector('[data-kx-qform-state="success"]');
      if (success) {
        clearStore(this.storageKey);
        success.focus();
        return;
      }

      form.noValidate = true;
      this.items = Array.from(
        form.querySelectorAll('.kx-qform__field .kx-qform__input, [data-kx-choices], input[name="contact[Privacy consent]"]')
      );
      this.originalLabel = this.submitButton ? this.submitButton.textContent : '';
      this.items.forEach((item) => {
        if (item.getAttribute('aria-invalid') === 'true') this.flagged.add(item);
      });

      if (this.summary && this.summary.getAttribute('data-kx-qform-state') === 'error') {
        this.restore();
        this.summary.focus();
      } else {
        this.dropStale();
      }

      form.addEventListener('submit', this.onSubmit.bind(this));
      form.addEventListener('input', this.onChange.bind(this));
      form.addEventListener('change', this.onChange.bind(this));
      if (this.summary) this.summary.addEventListener('click', this.onSummaryClick.bind(this));
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.setSubmitting(false);
      });
    }

    /* Validation */

    inputsOf(item) {
      return item.matches('[data-kx-choices]') ? Array.from(item.querySelectorAll('input')) : [item];
    }

    focusTarget(item) {
      return this.inputsOf(item)[0];
    }

    contactMethodKey() {
      const checked = this.form.querySelector('input[name="contact[Preferred contact method]"]:checked');
      return checked ? checked.dataset.kxKey : '';
    }

    messageFor(item) {
      const fallback = (element) => element.validationMessage || '';

      if (item.matches('[data-kx-choices]')) {
        if (!item.hasAttribute('data-kx-required')) return '';
        const inputs = this.inputsOf(item);
        if (inputs.some((input) => input.checked)) return '';
        return item.dataset.kxError || fallback(inputs[0]);
      }

      if (item.type === 'checkbox') {
        return item.required && !item.checked ? item.dataset.kxError || fallback(item) : '';
      }

      const value = item.value.trim();
      const formatError = item.dataset.kxErrorFormat || fallback(item);

      if (!value) {
        if (item.required) return item.dataset.kxError || fallback(item);
        if (item.dataset.kxValidate === 'phone' && PHONE_METHODS.includes(this.contactMethodKey())) {
          return item.dataset.kxErrorRequired || formatError;
        }
        return '';
      }

      if (item.type === 'email' && item.validity.typeMismatch) return formatError;

      switch (item.dataset.kxValidate) {
        case 'digits':
          return /^\d+$/.test(value) && parseInt(value, 10) >= 1 ? '' : formatError;
        case 'https':
          return isHttpsUrl(value) ? '' : formatError;
        case 'phone': {
          const digits = value.replace(/\D/g, '');
          return /^[0-9+().\-\s]+$/.test(value) && digits.length >= 7 ? '' : formatError;
        }
        default:
          return '';
      }
    }

    errorElementOf(item) {
      return document.getElementById(item.id + '-error');
    }

    showError(item, message) {
      const errorElement = this.errorElementOf(item);
      if (errorElement) {
        const text = errorElement.querySelector('[data-kx-error-text]');
        if (text) text.textContent = message;
        errorElement.hidden = false;
      }
      this.inputsOf(item).forEach((input) => {
        input.setAttribute('aria-invalid', 'true');
        if (errorElement) addToken(input, 'aria-describedby', errorElement.id);
      });
      this.flagged.add(item);
    }

    clearError(item) {
      const errorElement = this.errorElementOf(item);
      if (errorElement) errorElement.hidden = true;
      this.inputsOf(item).forEach((input) => {
        input.removeAttribute('aria-invalid');
        if (errorElement) removeToken(input, 'aria-describedby', errorElement.id);
      });
      this.flagged.delete(item);
      this.removeSummaryEntry(item);
    }

    validateAll() {
      const errors = [];
      this.items.forEach((item) => {
        const message = this.messageFor(item);
        if (message) {
          this.showError(item, message);
          errors.push({ item, message });
        } else {
          this.clearError(item);
        }
      });
      return errors;
    }

    /* Summary */

    renderSummary(errors) {
      if (!this.summary) return;
      const title = this.summary.querySelector('[data-kx-summary-title]');
      const list = this.summary.querySelector('[data-kx-summary-list]');
      if (title && this.summary.dataset.heading) title.textContent = this.summary.dataset.heading;
      if (list) {
        list.textContent = '';
        errors.forEach(({ item, message }) => {
          const entry = document.createElement('li');
          const link = document.createElement('a');
          link.href = '#' + this.focusTarget(item).id;
          link.textContent = message;
          entry.appendChild(link);
          list.appendChild(entry);
        });
      }
      this.summary.removeAttribute('data-kx-qform-state');
      this.summary.hidden = errors.length === 0;
    }

    removeSummaryEntry(item) {
      if (!this.summary || this.summary.hidden) return;
      const target = this.focusTarget(item);
      if (!target) return;
      const link = this.summary.querySelector('a[href="#' + target.id + '"]');
      if (link) link.parentElement.remove();
      const list = this.summary.querySelector('[data-kx-summary-list]');
      if (list && !list.children.length) this.summary.hidden = true;
    }

    onSummaryClick(event) {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;
      const target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target) return;
      event.preventDefault();
      this.focusField(target);
    }

    focusField(element) {
      element.focus({ preventScroll: true });
      element.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    /* Events */

    onChange(event) {
      const target = event.target;
      const item = this.items.find((candidate) => candidate === target || (candidate.matches('[data-kx-choices]') && candidate.contains(target)));
      if (item && this.flagged.has(item)) {
        const message = this.messageFor(item);
        if (message) {
          this.showError(item, message);
        } else {
          this.clearError(item);
        }
      }

      if (target.name === 'contact[Preferred contact method]') {
        const phone = this.items.find((candidate) => candidate.dataset && candidate.dataset.kxValidate === 'phone');
        if (phone && this.flagged.has(phone) && !this.messageFor(phone)) this.clearError(phone);
      }
    }

    onSubmit(event) {
      if (this.form.dataset.submitting === 'true') {
        event.preventDefault();
        return;
      }

      const errors = this.validateAll();
      if (errors.length) {
        event.preventDefault();
        this.renderSummary(errors);
        this.focusField(this.focusTarget(errors[0].item));
        return;
      }

      this.renderSummary([]);
      this.save();
      this.setSubmitting(true);
    }

    setSubmitting(active) {
      window.clearTimeout(this.resetTimer);
      if (!this.submitButton) return;

      if (active) {
        this.form.dataset.submitting = 'true';
        this.submitButton.setAttribute('aria-disabled', 'true');
        const label = this.submitButton.dataset.submittingLabel;
        if (label) this.submitButton.textContent = label;
        if (this.liveRegion) this.liveRegion.textContent = this.liveRegion.dataset.text || label || '';
        this.resetTimer = window.setTimeout(() => this.setSubmitting(false), SAFETY_RESET);
      } else {
        delete this.form.dataset.submitting;
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.textContent = this.originalLabel;
        if (this.liveRegion) this.liveRegion.textContent = '';
      }
    }

    /* Value restore */

    save() {
      const values = {};
      Array.from(this.form.elements).forEach((element) => {
        if (!isStorable(element)) return;
        if (element.type === 'checkbox') {
          values[element.name] = element.checked;
        } else if (element.type === 'radio') {
          if (element.checked) values[element.name] = element.value;
        } else {
          values[element.name] = element.value;
        }
      });
      writeStore(this.storageKey, { t: Date.now(), v: values });
    }

    restore() {
      const entry = readStore(this.storageKey);
      if (!entry || !entry.v || Date.now() - entry.t > MAX_AGE) {
        clearStore(this.storageKey);
        return;
      }
      Array.from(this.form.elements).forEach((element) => {
        if (!isStorable(element) || !Object.prototype.hasOwnProperty.call(entry.v, element.name)) return;
        const value = entry.v[element.name];
        if (element.type === 'checkbox') {
          element.checked = value === true;
        } else if (element.type === 'radio') {
          element.checked = element.value === value;
        } else if (typeof value === 'string') {
          element.value = value;
        }
      });
    }

    dropStale() {
      const entry = readStore(this.storageKey);
      if (entry && (!entry.t || Date.now() - entry.t > MAX_AGE)) clearStore(this.storageKey);
    }
  }

  function init(root) {
    root.querySelectorAll('form[data-kx-qform]').forEach((form) => {
      if (form.dataset.kxQformReady) return;
      form.dataset.kxQformReady = 'true';
      new QuoteForm(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
